import moment from "moment";
import { formatMoney } from "../../features/utils/formatMoney";
import { getSupplierName } from "../flight/supplier/getSupplierName";
import { getSupplierClass } from "../../components/flight/FlightOfferDisplay";
import { flightStatusMap } from "../../pages/Orders/OrdersData";
import Icon from "../../components/HOC/Icon";
import { Link } from "react-router-dom";
import { getChangeStatusName } from "../flight/order/getStatusName";
import OrderStatus from "../../components/order/OrderStatus";
import { getFlightFareTypeTagElement } from "./flightFareTypeTag";
import {
  getChangeRequestPassengers,
  getOpenTicketTripSummary,
} from "../flight/order/getRequestedDateChange";

export function templateFlightOrderOpenTicketData(data) {
  const {
    flightType,
    from,
    to,
    firstFlight,
    booking: flightData,
    directionNote,
  } = getOpenTicketTripSummary(data);
  const travelDate = firstFlight?.departureDate
    ? moment(firstFlight.departureDate).isValid()
      ? moment(firstFlight.departureDate).format("YYYY-MM-DD")
      : firstFlight.departureDate
    : "";
  const passengerNames = getChangeRequestPassengers(data, flightData);
  const passengers = passengerNames.join(", ");
  const passengerNote =
    Array.isArray(data?.passengerInfo) && data.passengerInfo.length
      ? data.passengerInfo.length === 1
        ? "1 passenger on this open"
        : `${data.passengerInfo.length} passengers on this open`
      : "";

  return [
    {
      elements: [
        {
          type: "title",
          value: moment(data?.createdAt)?.format("DD-MM-YYYY HH:mm [GMT]Z"),
        },
        {
          type: "copy",
          value: data?.orderId,
          renderValue: () => (
            <Link
              target="_blank"
              to={
                "/order/flight/ticket/changedate/confirmation/" +
                data?._id +
                "?page=Open Ticket"
              }
            >
              {data?.orderId}
            </Link>
          ),
        },
        {
          type: "tag",
          value: getSupplierName(flightData?.supplier),
          className: getSupplierClass(flightData?.supplier),
          color: "text-[#BD002A]",
          background: "bg-[#BD002A]/20",
        },
        {
          type: "title",
          value: "",
        },
        getFlightFareTypeTagElement(flightData),
        {
          type: "Agent",
          render: () => (
            <div className="py-2">
              <small>
                {(data?.account?.firstName || "") +
                  " " +
                  (data?.account?.lastName || "")}
              </small>
              <div
                className="w-[200px] overflow-hidden whitespace-nowrap text-ellipsis"
                title={data?.account?.detail?.tradingName}
              >
                {data?.account?.detail?.tradingName}
              </div>
            </div>
          ),
        },
      ],
      value: moment(data?.createdAt)?.format("YYYY-MM-DD HH:mm"),
      width: "md:w-[25%]",
    },
    {
      elements: [
        { type: "title", value: "Request Time" },
        {
          type: "text",
          value: moment(data?.updatedAt)?.format("YYYY-MM-DD HH:mm"),
        },
      ],
      value: moment(data?.updatedAt)?.format("YYYY-MM-DD HH:mm"),
      width: "md:w-[20%]",
    },
    {
      elements: [
        { type: "title", value: flightType },
        {
          type: "text",
          render: () => (
            <div>
              {from && to ? `${from} - ${to}` : ""}
              <div>
                <span className="text-theme1">
                  {flightData?.pnr || data?.bookingId || ""}
                </span>
              </div>
              <small>
                {firstFlight?.airlineName || flightData?.flights?.at(0)?.airlineName}
              </small>
              {directionNote ? (
                <div>
                  <small className="text-amber-700">{directionNote}</small>
                </div>
              ) : null}
            </div>
          ),
        },
      ],
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "Travel Time" },
        {
          type: "text",
          value: travelDate ? travelDate + " " : "",
        },
        {
          type: "text",
          value: firstFlight?.departureTime ? (
            <span className="text-blue-500">
              {firstFlight?.departureTime +
                (firstFlight?.arrivalTime ? " - " + firstFlight.arrivalTime : "")}
            </span>
          ) : (
            ""
          ),
        },
      ],
      value: travelDate,
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "GDS PNR" },
        { type: "copy", value: flightData?.pnr },
        { type: "title", value: "Airline PNR" },
        { type: "copy", value: flightData?.pnr },
      ],
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "Passenger" },
        {
          type: "text",
          value: passengers || "-",
        },
        passengerNote
          ? {
              type: "text",
              value: <small className="text-gray-500">{passengerNote}</small>,
            }
          : { type: "text", value: "" },
      ],
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "Fee" },
        {
          type: "text",
          value: data?.payable ? formatMoney(data?.payable) : "Free",
        },
      ],
      value: data?.payable,
      width: "md:w-[15%]",
    },
    {
      elements: [
        { type: "title", value: "Status" },
        {
          type: "status",
          value: getChangeStatusName(flightStatusMap(data?.status), data),
          render: () => {
            const status = flightStatusMap(data?.status);
            const assigned =
              ["Approved", "Pending", "ManualReviewRequired"].includes(status) &&
              data?.tasks?.at(0);
            return (
              <div className={`rounded-md mb-3`}>
                <span
                  className={`inline-block overflow-hidden text-ellipsis text-xs `}
                  title={status}
                >
                  {assigned ? (
                    ["Approved", "Pending"]?.includes(status) ? (
                      !["ToDo"]?.includes(assigned?.taskStatus) ? (
                        `Awaiting Quote by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                      ) : ["ToDo"]?.includes(assigned?.taskStatus) ? (
                        `Awaiting acceptance by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                      ) : (
                        `Assigned to ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                      )
                    ) : (
                      `Under Review by ${assigned?.assignee?.firstName} ${assigned?.assignee?.lastName}`
                    )
                  ) : (
                    <OrderStatus value={getChangeStatusName(status, data)} />
                  )}
                </span>
              </div>
            );
          },
        },
        {
          type: "time",
          render: () => {
            const diffInSeconds = moment
              .utc(flightData?.ticketingExpiry)
              .diff(moment.utc(), "seconds");

            const hours = Math.floor(diffInSeconds / 3600);
            const minutes = Math.floor((diffInSeconds % 3600) / 60);

            return (hours || minutes) > 0 ? (
              <div className="flex gap-1 items-center">
                <Icon icon="mdi:clock-outline" className="!w-4 !h-4" />
                {hours || minutes} {hours ? "Hrs" : "Mins"}
              </div>
            ) : null;
          },
        },
      ],
      value: (function () {
        return ["Pending", "Denied", "Approved", "PendingVoid"]?.findIndex(
          (val) => val === flightStatusMap(data?.status),
        );
      })(),
      width: "md:w-[15%]",
    },
  ];
}
